import styled, { css } from "styled-components"
import { Modal, Avatar } from "@mantine/core"

// Design tokens
const COLORS = {
    text: {
        primary: "#212529",
        secondary: "#495057",
        muted: "#868e96",
    },
    background: {
        white: "#ffffff",
        light: "#f8f9fa",
        lighter: "#e9ecef",
    },
    primary: "#0066cc",
    primaryDark: "#0052a3",
}

const SPACING = {
    xs: "4px",
    sm: "8px",
    md: "12px",
    lg: "16px",
    xl: "24px",
    xxl: "32px",
}

const BORDER_RADIUS = {
    sm: "8px",
    md: "12px",
    lg: "16px",
}

export const StyledModal = styled(Modal)`
    & .mantine-Modal-content {
        border-radius: ${BORDER_RADIUS.lg};
        width: 800px !important;
        max-width: 90vw;
        max-height: 90vh;
        overflow: visible;
        min-height: 400px;
    }

    & .mantine-Modal-inner {
        padding: 20px;
    }

    & .mantine-Modal-header {
        padding: 20px ${SPACING.xl} ${SPACING.md} ${SPACING.xl};
        flex-shrink: 0;
        border-top-left-radius: ${BORDER_RADIUS.lg};
        border-top-right-radius: ${BORDER_RADIUS.lg};
    }

    & .mantine-Modal-title {
        font-size: 24px;
        font-weight: 600;
        color: ${COLORS.text.primary};
        width: 100%;
    }

    & .mantine-Modal-close {
        color: ${COLORS.text.muted};

        &:hover {
            background-color: ${COLORS.background.light};
        }
    }

    & .mantine-Modal-body {
        padding: 0;
        overflow: visible;
        max-height: none;
    }
`

export const ModalContent = styled.div`
    display: grid;
    grid-template-columns: 180px 1fr;
    gap: ${SPACING.xxl};
    padding: ${SPACING.md} ${SPACING.xl} ${SPACING.xl} ${SPACING.xl};
    min-height: 300px;
    width: 100%;

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
    }
`

export const LeftColumn = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${SPACING.sm};
    align-items: stretch;
`

export const RightColumn = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${SPACING.lg};
`

export const ProfileSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${SPACING.sm};
    align-items: center;
    max-width: 180px;
    width: 100%;
`

export const StyledAvatar = styled(Avatar)`
    width: 100%;
    max-width: 180px;
    height: auto;
    aspect-ratio: 3 / 4;
    border-radius: ${BORDER_RADIUS.sm};
    margin: 0 auto;

    & .mantine-Avatar-placeholder {
        background-color: ${COLORS.background.lighter};
        color: ${COLORS.text.muted};
        font-size: 32px;
    }

    img {
        object-fit: cover;
    }
`

// Base badge styles
const baseBadgeStyles = css`
    padding: ${SPACING.xs} ${SPACING.md};
    border-radius: ${BORDER_RADIUS.md};
    font-size: 12px;
    font-weight: 500;
    text-align: center;
    text-transform: uppercase;
    width: 100%;
`

export const RoleBadge = styled.div`
    ${baseBadgeStyles}
    background-color: ${COLORS.background.lighter};
    color: ${COLORS.text.secondary};
`

export const PartyBadge = styled.div`
    ${baseBadgeStyles}
    background-color: ${COLORS.primary};
    color: ${COLORS.background.white};
    font-weight: 600;
`

export const DetailsSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${SPACING.lg};
`

export const DetailRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: ${SPACING.sm};
`

export const DetailLabel = styled.span`
    font-size: 16px;
    color: ${COLORS.text.secondary};
    font-weight: 400;
    min-width: 120px;
`

export const DetailValue = styled.span`
    font-size: 16px;
    color: ${COLORS.text.primary};
    font-weight: 500;
    text-align: right;
    flex: 1;
    word-break: break-word;
`

export const WebsiteLink = styled.a`
    color: ${COLORS.primary};
    text-decoration: underline;
    font-size: 16px;
    font-weight: 500;

    &:hover {
        color: ${COLORS.primaryDark};
    }

    &:visited {
        color: ${COLORS.primary};
    }
`

export const SocialLinksSection = styled.div`
    display: flex;
    gap: ${SPACING.sm};
    justify-content: center;
`

export const SocialLink = styled.a`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background-color: ${COLORS.background.white};
    color: ${COLORS.text.secondary};
    transition: all 0.2s ease;
    text-decoration: none;

    &:hover {
        background-color: ${COLORS.background.light};
        color: ${COLORS.text.primary};
        text-decoration: none;
    }

    i {
        font-size: 18px;
    }
`
